import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "catalyst-ui-kit/badge";
import { Button } from "catalyst-ui-kit/button";
import { Input } from "catalyst-ui-kit/input";
import PrettyDate from "components/format/PrettyDate";
import { fetchFiles } from "services/files-category.service";
import {
  fetchDocumentAnnulments,
  setAnnulmentReplacement,
  resendAnnulmentNotices,
  downloadAnnulmentOriginal,
} from "services/upload-file.service";

// Mirrors the backend search rules of files/fetch — the server only
// searches past 3 characters, so keep the client in sync.
const SEARCH_MIN_LENGTH = 4;
const DEBOUNCE_DELAY = 300;

interface Recipient {
  name: string;
  email: string;
  role: "signer" | "receiver";
  signedAt?: string | null;
  status: "sent" | "failed" | "pending" | "not_sent";
  error?: string | null;
}

interface AnnulmentRow {
  id: string;
  kind: "annulled" | "resign";
  source?: string | null;
  reason?: string | null;
  createdAt: string;
  annulledBy: { id: string; name: string } | null;
  replacementFile: { id: string; name: string } | null;
  hasSignedOriginal: boolean;
  recipients?: Recipient[];
}

interface ReplacesRow {
  annulmentId: string;
  // null when the annulled file it points at was itself removed/not found.
  file: { id: string; name: string; annulledAt?: number | null } | null;
  createdAt: string;
}

interface AnnulmentsResponse {
  annulments: AnnulmentRow[];
  replaces: ReplacesRow[];
  canManage: boolean;
}

interface SearchResultFile {
  id: string;
  name?: string;
  title?: string;
}

interface DocumentAnnulmentPanelProps {
  companyId: string;
  fileId: string;
  /** Used only to name the file when downloading a signed original. */
  fileName?: string;
  /** Bump to force a refetch (e.g. right after annulling the document). */
  refreshKey?: number;
  /**
   * The file's own `annulledAt`, known from the page's file details without
   * a round trip. Used only as a fallback: if the annulments fetch fails, we
   * still know from this whether the document is annulled and can show a
   * bare notice instead of silently rendering nothing.
   */
  annulledAt?: number | null;
}

const recipientStatusColor: Record<Recipient["status"], "green" | "red" | "amber" | "zinc"> = {
  sent: "green",
  failed: "red",
  pending: "amber",
  not_sent: "zinc",
};

function RecipientsTable({ recipients }: { recipients: Recipient[] }) {
  const { t } = useTranslation();
  if (!recipients.length) return null;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[480px] text-left text-sm">
        <thead>
          <tr className="border-b border-zinc-950/10 text-xs text-zinc-500 dark:border-white/10">
            <th className="py-1.5 pr-4 font-medium">{t("name")}</th>
            <th className="py-1.5 pr-4 font-medium">{t("email")}</th>
            <th className="py-1.5 pr-4 font-medium">{t("annulment_recipient_role")}</th>
            <th className="py-1.5 font-medium">{t("status")}</th>
          </tr>
        </thead>
        <tbody>
          {recipients.map((recipient, index) => (
            <tr
              key={`${recipient.email}-${index}`}
              className="border-b border-zinc-950/5 last:border-b-0 dark:border-white/5"
            >
              <td className="py-1.5 pr-4">{recipient.name || "-"}</td>
              <td className="py-1.5 pr-4">{recipient.email}</td>
              <td className="py-1.5 pr-4">
                {recipient.role === "signer"
                  ? t("annulment_recipient_role_signer")
                  : t("annulment_recipient_role_receiver")}
                {recipient.role === "signer" && recipient.signedAt && (
                  <span className="text-zinc-500">
                    {" "}
                    ({recipient.signedAt})
                  </span>
                )}
              </td>
              <td className="py-1.5">
                <Badge color={recipientStatusColor[recipient.status]}>
                  {t(`annulment_recipient_status_${recipient.status}`)}
                </Badge>
                {recipient.status === "failed" && recipient.error && (
                  <span className="ml-2 text-xs text-red-600 dark:text-red-400">
                    {recipient.error}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Inline picker (not a dialog) that searches with the document search (files/fetch). */
function ReplacementPicker({
  companyId,
  onPick,
  onCancel,
}: {
  companyId: string;
  onPick: (file: SearchResultFile) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResultFile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchSeqRef = useRef(0);

  const runSearch = useCallback(
    debounce(async (value: string) => {
      const seq = searchSeqRef.current;
      if (!companyId || value.trim().length < SEARCH_MIN_LENGTH) {
        setResults([]);
        setIsSearching(false);
        return;
      }
      try {
        const { filesCollection } = await fetchFiles(
          companyId,
          { textSearch: value },
          "document"
        );
        if (seq !== searchSeqRef.current) return;
        setResults(filesCollection || []);
        setIsSearching(false);
      } catch {
        if (seq !== searchSeqRef.current) return;
        setResults([]);
        setIsSearching(false);
      }
    }, DEBOUNCE_DELAY),
    [companyId]
  );

  const handleChange = (value: string) => {
    setQuery(value);
    searchSeqRef.current += 1;
    if (value.trim().length >= SEARCH_MIN_LENGTH) {
      setIsSearching(true);
      runSearch(value);
    } else {
      setResults([]);
      setIsSearching(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-zinc-950/10 p-3 dark:border-white/10">
      <Input
        type="text"
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={t("attach_to_document_search_placeholder")}
        autoFocus
      />
      {query.trim().length >= SEARCH_MIN_LENGTH && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-zinc-950/10 dark:border-white/10">
          {isSearching ? (
            <div className="p-3 text-sm text-zinc-500">{t("loading")}</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-zinc-500">{t("no_documents_found")}</div>
          ) : (
            results.map((file) => (
              <button
                key={file.id}
                type="button"
                onClick={() => onPick(file)}
                className="block w-full border-b border-zinc-950/5 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-zinc-950/5 dark:border-white/5 dark:hover:bg-white/5"
              >
                {file.name || file.title}
              </button>
            ))
          )}
        </div>
      )}
      <div className="mt-2">
        <Button plain onClick={onCancel}>
          {t("cancel")}
        </Button>
      </div>
    </div>
  );
}

function AnnulmentRowCard({
  row,
  companyId,
  fileId,
  fileName,
  canManage,
  onChanged,
}: {
  row: AnnulmentRow;
  companyId: string;
  fileId: string;
  fileName?: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const isAnnulled = row.kind === "annulled";
  const hasUnsentRecipients = (row.recipients || []).some(
    (recipient) => recipient.status !== "sent"
  );

  const handlePick = async (file: SearchResultFile) => {
    setIsBusy(true);
    try {
      await setAnnulmentReplacement(companyId, fileId, row.id, file.id);
      setIsPickerOpen(false);
      onChanged();
    } catch (e: any) {
      toast.error(t(e?.response?.data?.message || "error"));
    } finally {
      setIsBusy(false);
    }
  };

  const handleRemoveReplacement = async () => {
    setIsBusy(true);
    try {
      await setAnnulmentReplacement(companyId, fileId, row.id, null);
      onChanged();
    } catch (e: any) {
      toast.error(t(e?.response?.data?.message || "error"));
    } finally {
      setIsBusy(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await resendAnnulmentNotices(companyId, fileId, row.id);
      toast.success(t("annulment_notices_resent"));
      onChanged();
    } catch (e: any) {
      toast.error(t(e?.response?.data?.message || "error"));
    } finally {
      setIsResending(false);
    }
  };

  const handleDownloadOriginal = async () => {
    setIsDownloading(true);
    try {
      await downloadAnnulmentOriginal(
        companyId,
        fileId,
        row.id,
        fileName ? `${fileName}.pdf` : undefined
      );
    } catch (e: any) {
      toast.error(t(e?.response?.data?.message || "error"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={clsx(
        "rounded-lg border p-4",
        isAnnulled
          ? "border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30"
          : "border-zinc-950/10 bg-zinc-50 dark:border-white/10 dark:bg-white/5"
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {isAnnulled ? (
          <Badge color="red">{t("annulled_badge")}</Badge>
        ) : (
          <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
            {t("annulment_panel_resign_title")}
          </span>
        )}
        <span className="text-sm text-zinc-500">
          {row.annulledBy?.name || t("annulment_panel_unknown_actor")}
          {" — "}
          <PrettyDate dateString={row.createdAt} showTime />
        </span>
      </div>
      {row.reason && (
        <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
          <strong>{t("annulment_reason_label")}:</strong> {row.reason}
        </p>
      )}

      {isAnnulled && (
        <div className="mt-2 text-sm">
          {row.replacementFile ? (
            <p className="text-zinc-700 dark:text-zinc-300">
              {t("annulment_panel_replaced_by")}{" "}
              <RouterLink
                className="text-sky-700 hover:underline"
                to={`/app/${companyId}/documents/view/${row.replacementFile.id}`}
              >
                {row.replacementFile.name}
              </RouterLink>
              {/* On a phone the actions get their own row: inside the sentence
                  they were 20px tall and the destructive one sat right against
                  the harmless one. From md up they stay inline after it. */}
              {canManage && (
                <span className="max-md:mt-1 max-md:flex max-md:flex-wrap max-md:gap-x-4">
                  <span className="max-md:hidden">{" · "}</span>
                  <button
                    type="button"
                    className="text-sky-700 hover:underline max-md:py-3"
                    onClick={() => setIsPickerOpen(true)}
                    disabled={isBusy}
                  >
                    {t("annulment_panel_change_replacement")}
                  </button>
                  <span className="max-md:hidden">{" · "}</span>
                  <button
                    type="button"
                    className="text-red-700 hover:underline max-md:py-3"
                    onClick={handleRemoveReplacement}
                    disabled={isBusy}
                  >
                    {t("annulment_panel_remove_replacement")}
                  </button>
                </span>
              )}
            </p>
          ) : (
            canManage && (
              <button
                type="button"
                className="text-sky-700 hover:underline max-md:py-3"
                onClick={() => setIsPickerOpen(true)}
              >
                {t("annulment_panel_set_replacement")}
              </button>
            )
          )}
          {isPickerOpen && (
            <ReplacementPicker
              companyId={companyId}
              onPick={handlePick}
              onCancel={() => setIsPickerOpen(false)}
            />
          )}
        </div>
      )}

      {canManage && (
        <>
          <RecipientsTable recipients={row.recipients || []} />
          <div className="mt-3 flex flex-wrap gap-3">
            {hasUnsentRecipients && (
              <Button outline onClick={handleResend} disabled={isResending}>
                {t("annulment_panel_resend")}
              </Button>
            )}
            {row.hasSignedOriginal && (
              <Button outline onClick={handleDownloadOriginal} disabled={isDownloading}>
                {t("annulment_panel_download_original")}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Shows a document's annulment/resign history (and, if it replaces an
 * annulled document, that link too). Renders nothing when there is nothing
 * to show — a document with no annulment history stays visually unchanged.
 */
export default function DocumentAnnulmentPanel({
  companyId,
  fileId,
  fileName,
  refreshKey,
  annulledAt,
}: DocumentAnnulmentPanelProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<AnnulmentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Guards against an older, slower response (previous file or refresh)
  // overwriting the history of the document now on screen.
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    if (!companyId || !fileId) return;
    const seq = ++loadSeqRef.current;
    setIsLoading(true);
    try {
      const response = await fetchDocumentAnnulments(companyId, fileId);
      if (seq !== loadSeqRef.current) return;
      setData(response);
      setHasError(false);
    } catch {
      if (seq !== loadSeqRef.current) return;
      setData(null);
      setHasError(true);
    } finally {
      if (seq === loadSeqRef.current) setIsLoading(false);
    }
  }, [companyId, fileId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const annulments = data?.annulments || [];
  const replaces = data?.replaces || [];
  const canManage = !!data?.canManage;

  // The fetch failed but the file itself is known to be annulled (passed down
  // from the page's own file details) — do not silently render nothing, show
  // a bare notice with a retry instead of pretending the document is fine.
  if (hasError && !isLoading && annulledAt) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-950/30">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge color="red">{t("annulled_badge")}</Badge>
            <span className="text-sm text-red-700 dark:text-red-300">
              {t("annulment_panel_load_error")}
            </span>
          </div>
          <Button outline onClick={load}>
            {t("retry", { defaultValue: "Retry" })}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || (annulments.length === 0 && replaces.length === 0)) {
    return null;
  }

  return (
    <div className="rounded-lg border border-zinc-950/10 bg-white p-4 dark:border-white/10 dark:bg-zinc-900">
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">
          {t("annulment_panel_title")}
        </h2>
        {replaces.map((row) =>
          row.file ? (
            <div
              key={row.annulmentId}
              className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-200"
            >
              {t("annulment_panel_replaces")}{" "}
              <RouterLink
                className="font-medium hover:underline"
                to={`/app/${companyId}/documents/view/${row.file.id}`}
              >
                {row.file.name}
              </RouterLink>
            </div>
          ) : null
        )}
        {annulments.map((row) => (
          <AnnulmentRowCard
            key={row.id}
            row={row}
            companyId={companyId}
            fileId={fileId}
            fileName={fileName}
            canManage={canManage}
            onChanged={load}
          />
        ))}
      </div>
    </div>
  );
}
